import * as React from 'react'
import cx from 'classnames'

import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from 'components/ModalSheet'
import Button from 'components/Button'

import styles from './ConsultationModal.sass'
import { format } from 'date-fns'
import CompletionToggle from 'components/CompletionToggle'
import { observer } from 'mobx-react'
import Avatar from 'components/Avatar'
import { TimeRange } from 'jobs/TimeRange'
import ConsultationModalViewModel from './ConsultationModal'

interface Props {
  vm: ConsultationModalViewModel
}

export default observer(function ConsultationModal({ vm }: Props) {
  return (
    <ModalSheet showing={vm.showing} onRequestClose={vm.close}>
      <ModalSheetHeader withCloseButton></ModalSheetHeader>
      <ModalSheetContent leftAligned>
        <ModalSheetContentBlock>
          <ModalSheetContentTitle>
            <span className={styles.title}>Schedule a consultation call</span>
          </ModalSheetContentTitle>

          <ModalSheetContentDescription>
            <span className={styles.description}>
              {`This job requires a ${vm.requestedLength} minute consultation call. To confirm job
              pickup, select a date and time to continue.`}
            </span>
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
        <div className={styles.scheduledDays}>
          {vm.availableDays.map((d) => (
            <div key={d.formattedDay.valueOf()}>
              <div className={styles.dayTitle}>{d.formattedDay}</div>
              {d?.timeSlots?.map((t, i) => (
                <TimeSlotListItem key={i} index={i} vm={vm} range={t} />
              ))}
            </div>
          ))}
        </div>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button type="default" loading={vm.submit.pending} onClick={vm.close}>
            Close
          </Button>
          <Button
            submit
            type="primary"
            onClick={vm.submit}
            data-test="confirm-pickup-button"
          >
            Confirm Pickup
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

interface TimeSlotListItemProps {
  index: number
  range: TimeRange
  vm: ConsultationModalViewModel
}

const TimeSlotListItem = observer(function TimeSlotListItem({
  index,
  range,
  vm,
}: TimeSlotListItemProps) {
  const bookedConsultation = vm.bookedConsultations.find((c) =>
    c.bookedTime?.isWithin(range),
  )

  const isBooked = Boolean(bookedConsultation)
  const selected =
    vm.selectedTimeSlot?.isEqual(range) || Boolean(bookedConsultation)

  return (
    <div
      className={cx(
        styles.timeSlotItem,
        selected && styles.selected,
        isBooked && styles.disabled,
      )}
      data-test={`time-slot-item-${range.startTime.getDay()}-${index}`}
      onClick={() => !isBooked && vm.selectTimeSlot(range)}
    >
      <div className={styles.completionToggle}>
        <CompletionToggle
          checkType="tick"
          size={20}
          completed={selected}
          disabled={isBooked}
        />
      </div>
      <div className={styles.timeSlotText}>
        {selected && (
          <div className={styles.jobName}>
            {isBooked ? 'Unavailable' : 'Selected'}
          </div>
        )}
        {format(range.startTime, 'h:mm')} - {format(range.endTime, 'h:mmaaa')}
      </div>
      {selected && (
        <>
          <Avatar subject={vm.client} size={24} greyscale={isBooked} />
          <div className={styles.avatars}>
            <Avatar subject={vm.currentMember} size={24} greyscale={isBooked} />
          </div>
        </>
      )}
    </div>
  )
})
